import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { useCallback, useMemo, useState } from 'react'
import Tab from 'react-bootstrap/Tab'
import Tabs from 'react-bootstrap/Tabs'
import ECharts from '~/components/echarts'
import Select from '~/components/select'
import {
  awsRDSAcuUtilizationMetricsResourceIdChartPath,
  awsRDSCpuUtilizationMetricsResourceIdChartPath,
  awsRDSFreeStorageMetricsResourceIdChartPath,
  awsRDSIopsMetricsResourceIdChartPath,
  awsRDSMemoryMetricsResourceIdChartPath,
  awsRDSThroughputMetricsResourceIdChartPath,
} from '~/routes'

export default function AWSRDSMetricsCharts({ organizationId: orgId, resourceId: reId, dbInstanceClass }) {
  const [params, setParams] = useState({ metrics_time_frame: '1w' })
  const metricsTimeFrames = useMemo(
    () => [
      { label: t`1 Week`, value: '1w', default: true },
      { label: t`1 Month`, value: '1m' },
      { label: t`3 Months`, value: '3m' },
    ],
    [],
  )

  const onChange = useCallback((name, values) => {
    // update params when a filter is selected
    setParams((prevParams) => ({ ...prevParams, [name]: values }))
  }, [])

  const awsRDSAcuUtilizationMetricsResourceIdChartUrl = useMemo(
    () => awsRDSAcuUtilizationMetricsResourceIdChartPath(orgId, reId),
    [orgId, reId],
  )
  const awsRDSCpuUtilizationMetricsResourceIdChartUrl = useMemo(
    () => awsRDSCpuUtilizationMetricsResourceIdChartPath(orgId, reId),
    [orgId, reId],
  )
  const awsRDSMemoryMetricsResourceIdChartUrl = useMemo(
    () => awsRDSMemoryMetricsResourceIdChartPath(orgId, reId),
    [orgId, reId],
  )
  const awsRDSIopsMetricsResourceIdChartUrl = useMemo(
    () => awsRDSIopsMetricsResourceIdChartPath(orgId, reId),
    [orgId, reId],
  )
  const awsRDSThroughputMetricsResourceIdChartUrl = useMemo(
    () => awsRDSThroughputMetricsResourceIdChartPath(orgId, reId),
    [orgId, reId],
  )
  const awsRDSFreeStorageMetricsResourceIdChartUrl = useMemo(
    () => awsRDSFreeStorageMetricsResourceIdChartPath(orgId, reId),
    [orgId, reId],
  )

  const isServerless = dbInstanceClass === 'db.serverless'

  return (
    <div className='card'>
      <h3 className='card-header'>
        <Trans>Cloudwatch Information</Trans>
      </h3>
      <div className='card-body'>
        <div className='row mb-3 gx-2 gy-2 align-items-center'>
          <div className='col-sm-4'>
            <Select
              label={t`Time Frame`}
              name='metrics_time_frame'
              onChange={onChange}
              options={metricsTimeFrames}
              selected={params.metrics_time_frame}
            />
          </div>
        </div>
        <Tabs
          id='uncontrolled-metrics-tabs'
          className='mb-3'
          defaultActiveKey={isServerless ? 'acu-utilization-tab' : 'cpu-consumption-tab'}
        >
          {isServerless && (
            <Tab eventKey='acu-utilization-tab' title={t`ACU Utilization`}>
              <div className='row g-0'>
                <div className='col-sm-12'>
                  <ECharts
                    title={t`ACU Utilization`}
                    chartInfoTitle='aws_rds_acu_utilization'
                    url={awsRDSAcuUtilizationMetricsResourceIdChartUrl}
                    params={params}
                  />
                </div>
              </div>
            </Tab>
          )}
          <Tab eventKey='cpu-consumption-tab' title={t`CPU Consumption`}>
            <div className='row g-0'>
              <div className='col-sm-12'>
                <ECharts
                  title={t`CPU Consumption`}
                  chartInfoTitle='aws_rds_cpu_consumption'
                  url={awsRDSCpuUtilizationMetricsResourceIdChartUrl}
                  params={params}
                />
              </div>
            </div>
          </Tab>
          <Tab eventKey='memory-tab' title={t`Memory`} mountOnEnter>
            <div className='row g-0'>
              <div className='col-sm-12'>
                <ECharts
                  title={t`Freeable Memory`}
                  chartInfoTitle='aws_rds_freeable_memory'
                  url={awsRDSMemoryMetricsResourceIdChartUrl}
                  params={params}
                />
              </div>
            </div>
          </Tab>
          <Tab eventKey='iops-tab' title={t`IOPS`} mountOnEnter>
            <div className='row g-0'>
              <div className='col-sm-12'>
                <ECharts
                  title={t`Disk Operations`}
                  chartInfoTitle='aws_rds_disk_operations'
                  url={awsRDSIopsMetricsResourceIdChartUrl}
                  params={params}
                />
              </div>
            </div>
          </Tab>
          <Tab eventKey='throughput-tab' title={t`Throughput`} mountOnEnter>
            <div className='row g-0'>
              <div className='col-sm-12'>
                <ECharts
                  title={t`Throughput`}
                  chartInfoTitle='aws_rds_throughput'
                  url={awsRDSThroughputMetricsResourceIdChartUrl}
                  params={params}
                />
              </div>
            </div>
          </Tab>
          {!isServerless && (
            <Tab eventKey='storage-tab' title={t`Storage`} mountOnEnter>
              <div className='row g-0'>
                <div className='col-sm-12'>
                  <ECharts
                    title={t`Free Storage Space`}
                    chartInfoTitle='aws_rds_free_storage_space'
                    url={awsRDSFreeStorageMetricsResourceIdChartUrl}
                    params={params}
                  />
                </div>
              </div>
            </Tab>
          )}
        </Tabs>
      </div>
    </div>
  )
}
